/* eslint-disable max-len */
export type MetricStatus = 'critical' | 'good' | 'great' | 'needs_attention'
export type CategoryId = 'completeness' | 'content' | 'reviews'
export interface ScoredItem {
  id: string
  name: string
  status: MetricStatus
  statusMessage: string | [string, number]
  explainer: string
  targetDescription?: string
  ctaLabel?: string
  ctaAction?: () => void
  statusComponent?: 'progressRing' | 'statusIcon'
  minValue?: number
  maxValue?: number
  statusValue?: number
  valueSuffix?: string
  minimizeComplete?: boolean
}

export interface ScoredCategory {
  id: CategoryId
  name: string
  progress: number
  issueCount: number
  items: ScoredItem[]
}

const calculateProgress = (items: ScoredItem[]): number => {
  if (items.length === 0) return 0
  const goodOrGreatCount = items.filter(item => item.status === 'good' || item.status === 'great').length
  return Math.round((goodOrGreatCount / items.length) * 100)
}

const countIssues = (items: ScoredItem[]): number => {
  return items.filter(item => item.status === 'critical' || item.status === 'needs_attention').length
}

const scoreReviewsWithText = (data: BusinessProfileData): ScoredItem => {
  const count = data.reviewsWithTextCount

  let status: MetricStatus
  let explainer: string
  let statusMessage: string
  if (count === 0) {
    status = 'critical'
    explainer = 'Reviews are one of the strongest trust signals on Google. Add a few to make it easier for new clients to choose you.'
    statusMessage = '0 reviews'
  }
  else if (count <= 10) {
    status = 'needs_attention'
    explainer = 'Great start — keep collecting reviews consistently so you look established in your area.'
    statusMessage = '1-10 reviews'
  }
  else if (count < 50) {
    status = 'good'
    explainer = 'Strong credibility. Keep a steady flow of reviews to maintain trust and visibility.'
    statusMessage = '11-50 reviews'
  }
  else {
    status = 'great'
    explainer = 'Excellent social proof. Maintain momentum with regular review requests.'
    statusMessage = '50+ reviews'
  }

  return {
    id: 'reviewsWithText',
    name: 'Reviews with text',
    status,
    statusMessage,
    explainer,
    ctaLabel: status === 'critical' || status === 'needs_attention' ? 'View reviews' : '',
    // ctaAction: status === 'critical' || status === 'needs_attention' ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: false,
  }
}

const scoreReviewConsistency = (data: BusinessProfileData): ScoredItem => {
  const count = data.reviewsWithin60DaysCount

  let status: MetricStatus
  let explainer: string
  let statusMessage: string | [string, number] = ['$0 reviews in the last 60 days', count]
  if (count === 0) {
    status = 'critical'
    explainer = 'Fresh reviews signal an active business and build trust fast.'
    statusMessage = 'No reviews in the last 60 days'
  }
  else if (count < 5) {
    status = 'needs_attention'
    explainer = 'Good start — try to make reviews more consistent so you don\'t go quiet for long periods.'
    if (count === 1) {
      statusMessage = '1 review in the last 60 days'
    }
  }
  else if (count < 15) {
    status = 'good'
    explainer = 'Nice rhythm. Keep it going by asking happy clients right after their appointment.'
  }
  else {
    status = 'great'
    explainer = 'Strong signal to new clients and to Google. Maintain a steady flow rather than big bursts.'
  }

  return {
    id: 'reviewConsistency',
    name: 'Review Consistency',
    status,
    statusMessage,
    explainer,
    targetDescription: 'Aim for 1-2 reviews per month.',
    ctaLabel: status === 'critical' || status === 'needs_attention' ? 'View recent bookings' : '',
    // ctaAction: status === 'critical' || status === 'needs_attention' ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: false,
  }
}

const scoreAverageRating = (data: BusinessProfileData): ScoredItem => {
  const rating = data.averageRating

  let status: MetricStatus
  let explainer: string
  let statusMessage: string
  if (!rating) {
    status = 'critical'
    explainer = 'Profiles without ratings are often skipped by potential clients.'
    statusMessage = 'No rating'
  }
  else if (rating && rating < 4.0) {
    status = 'needs_attention'
    explainer = 'A rating under 4.0 reduces bookings. Focus on fixing the experience issues that show up in reviews, reply to all reviews, and ask happy clients to leave feedback to rebalance your average.'
    statusMessage = 'Low rating'
  }
  else if (rating && rating <= 4.7) {
    status = 'good'
    explainer = 'Solid trust signal, but you may still lose to competitors with higher ratings. Keep quality consistent, reply to reviews, and ask for reviews regularly.'
    statusMessage = 'Good rating'
  }
  else {
    status = 'great'
    explainer = 'Brilliant — this is premium-level trust. Maintain with consistent experiences and regular review requests.'
    statusMessage = 'Excellent rating'
  }

  return {
    id: 'averageRating',
    name: 'Average Rating',
    status,
    statusMessage,
    explainer,
    targetDescription: rating && rating < 4.8 ? 'Aim for an average rating of 4.8+' : undefined,
    minValue: 1,
    maxValue: 5,
    statusValue: rating ?? 0,
    statusComponent: 'progressRing',
    minimizeComplete: false,
  }
}

const scoreReviewResponseRate = (data: BusinessProfileData): ScoredItem => {
  const rate = data.reviewResponseRate ?? 0

  let status: MetricStatus
  let explainer: string
  let statusMessage: string

  if (data.reviewsWithTextCount === 0) {
    status = 'critical'
    explainer = 'Reviews are one of the strongest trust signals on Google.'
    statusMessage = 'No reviews yet'
  }

  if (!rate) {
    status = 'critical'
    explainer = 'Replying to reviews is a strong trust signal. Start by responding to recent reviews, then keep it consistent going forward.'
    statusMessage = 'Not replying to reviews yet'
  }
  else if (rate < 50) {
    status = 'critical'
    explainer = 'Many reviews aren\'t getting a response, which can make the business feel less engaged. Make replies part of your routine.'
    statusMessage = 'Replies are rare'
  }
  else if (rate < 80) {
    status = 'needs_attention'
    explainer = 'You\'re replying sometimes, but consistency is what builds trust. Try to respond to new reviews regularly so clients see you\'re present and proactive.'
    statusMessage = 'Replies could be more consistent'
  }
  else {
    status = 'good'
    explainer = 'Great habit. Keep replying to reviews to reinforce trust, helps future clients choose you, and can turn a neutral review into a loyal customer.'
    statusMessage = 'Consistently replying'
  }

  return {
    id: 'reviewResponseRate',
    name: 'Response rate',
    status,
    statusMessage,
    explainer,
    statusComponent: 'progressRing',
    minValue: 0,
    maxValue: 100,
    statusValue: rate,
    minimizeComplete: false,
    valueSuffix: '%',
  }
}
const scorePhotoCount = (data: BusinessProfileData): ScoredItem => {
  const count = data.photosCount

  let status: MetricStatus
  let explainer: string
  let statusMessage: string | [string, number]
  if (count <= 2) {
    status = 'critical'
    explainer = 'A near-empty profile looks untrustworthy and won\'t compete in map results. Profiles with photos get more clicks. Add at least 10 to look established.'
    if (count === 1) {
      statusMessage = '1 photo - Your profile looks unfinished'
    } else {
      statusMessage = ['$0 photos - Your profile looks unfinished', count]
    }
  }
  else if (count <= 9) {
    status = 'needs_attention'
    explainer = 'Add a mix of photos: your best work, your space, you at work, and entrance/signage so clients know what to expect.'
    statusMessage = ['$0 photos - Good start - add more variety', count]
  }
  else if (count <= 19) {
    status = 'good'
    explainer = 'You\'ve built trust. Keep improving by adding new work regularly and showcasing variety in styles and finishes.'
    statusMessage = ['$0 photos - Strong coverage - nearly “Great”', count]
  }
  else {
    status = 'great'
    explainer = 'Great gallery. Maintain momentum by adding 2-4 new photos per month (latest work + seasonal sets).'
    statusMessage = ['$0 photos added - Great coverage - keep it fresh', count]
  }

  return {
    id: 'photoCount',
    name: 'Photo Count',
    status,
    statusMessage,
    explainer,
    ctaLabel: status === 'critical' || status === 'needs_attention' ? 'Add photos' : '',
    // ctaAction: status === 'critical' || status === 'needs_attention' ? () => {} : undefined,
    statusComponent: 'statusIcon',
  }
}

const scoreClientPhotoCount = (data: BusinessProfileData): ScoredItem => {
  const count = data.clientPhotosCount

  let status: MetricStatus
  let explainer: string
  let statusMessage: string | [string, number]
  if (count === 0) {
    status = 'critical'
    explainer = 'Client result photos help new clients picture the outcome and trust your work. Add at least 6 photos to get started (with client consent).'
    statusMessage = '0 photos - no client photos yet'
  }
  else if (count <= 5) {
    status = 'needs_attention'
    explainer = 'Add more variety: different lengths, shapes, finishes, and nail art styles. Aim for 6-20.'
    if (count === 1) {
      statusMessage = '1 client photo added - Good start - add a few more'
    } else {
      statusMessage = ['$0 client photos added - Good start - add a few more', count]
    }
  }
  else if (count <= 20) {
    status = 'good'
    explainer = 'Strong social proof. Keep adding your best sets so clients can see your range and latest work.'
    statusMessage = ['$0 client photos added - Solid portfolio - keep building variety', count]
  }
  else {
    status = 'great'
    explainer = 'Excellent portfolio. Add 2-4 per month to stay current and show your newest work.'
    statusMessage = ['$0 client photos added - Strong social proof - keep it fresh', count]
  }

  return {
    id: 'clientPhotoCount',
    name: 'Client Photo Count',
    status,
    statusMessage,
    explainer,
    statusComponent: 'statusIcon',
  }
}

const scorePhotoRecency = (data: BusinessProfileData): ScoredItem => {
  const count = data.photosWithin60DaysCount

  let status: MetricStatus
  let explainer: string
  let statusMessage: string = `${count} photos in the last 60 days`
  if (count === 0) {
    status = 'critical'
    explainer = 'Fresh photos signal an active business and help clients trust your latest work. Add 3-5 new photos to get your profile moving again.'
    statusMessage = 'No photos in the last 60 days'
  }
  else if (count <= 5) {
    status = 'needs_attention'
    explainer = 'Aim for 3-9 photos every 60 days (about 1 per week) to stay competitive in local search.'
  }
  else if (count <= 15) {
    status = 'good'
    explainer = 'Keep it consistent: add 1-2 photos per week (latest sets, seasonal styles, your space).'
  }
  else {
    status = 'great'
    explainer = 'You\'re doing great. Maintain momentum with 2 photos per week when possible.'
  }

  return {
    id: 'photoRecency',
    name: 'Photo Recency',
    status,
    statusMessage,
    explainer,
    ctaLabel: status === 'critical' || status === 'needs_attention' ? 'Add photos' : '',
    // ctaAction: status === 'critical' || status === 'needs_attention' ? () => {} : undefined,
    statusComponent: 'statusIcon',
  }
}
// TODO: Needs attention
const scorePostCount = (data: BusinessProfileData): ScoredItem => {
  const count = data.postsCount

  let status: MetricStatus
  if (count === 0) {
    status = 'critical'
  }
  else if (count <= 2) {
    status = 'needs_attention'
  }
  else if (count <= 10) {
    status = 'good'
  }
  else {
    status = 'great'
  }

  return {
    id: 'postCount',
    name: 'Google Posts',
    status,
    statusMessage: count === 1 ? '1 post' : ['$0 posts', count],
    explainer: 'Active posting drives engagement and improves profile authority, aim for a regular posting pattern',
    ctaLabel: status === 'critical' || status === 'needs_attention' ? 'Add post' : '',
    // ctaAction: status === 'critical' || status === 'needs_attention' ? () => {} : undefined,
    statusComponent: 'statusIcon',
  }
}

const scorePostRecency = (data: BusinessProfileData): ScoredItem => {
  const lastPostDate = data.lastPostDate

  let status: MetricStatus
  let explainer: string
  let statusMessage: string
  if (!lastPostDate) {
    status = 'critical'
    explainer = 'Posting keeps your profile fresh and can drive clicks. Publish your first post today (availability, new work, offer, or update).'
    statusMessage = 'Never posted'
  }
  else if (lastPostDate < new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)) {
    status = 'needs_attention'
    explainer = 'Your profile looks inactive when posts go quiet. Aim to post every 2-4 weeks to stay top-of-mind.'
    statusMessage = 'Last post over 60 days ago'
  }
  else if (lastPostDate < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)) {
    status = 'good'
    explainer = 'Good — you\'ve posted recently. Keep it consistent with 1-2 posts per month.'
    statusMessage = 'Last post 30-60 days ago'
  }
  else {
    status = 'great'
    explainer = 'Great — your profile looks active. Maintain with monthly posts (or fortnightly when you can).'
    statusMessage = 'Posted in the last 30 days'
  }

  return {
    id: 'postRecency',
    name: 'Posts Recency',
    status,
    statusMessage,
    explainer,
    ctaLabel: status === 'critical' || status === 'needs_attention' ? 'Add posts' : '',
    // ctaAction: status === 'critical' || status === 'needs_attention' ? () => {} : undefined,
    statusComponent: 'statusIcon',
  }
}

const scoreDescription = (data: BusinessProfileData): ScoredItem => {
  const hasDescription = data.hasDescription

  return {
    id: 'description',
    name: 'Description',
    status: hasDescription ? 'good' : 'critical',
    statusMessage: hasDescription ? 'Description set' : 'No description',
    explainer: hasDescription
      ? 'A complete description improves search relevance and client understanding'
      : "Google can't properly categorise your services without a description",
    ctaLabel: hasDescription ? '' : 'Update description',
    // ctaAction: hasDescription ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: true,
  }
}

const scoreAddress = (data: BusinessProfileData): ScoredItem => {
  const hasAddress = data.hasAddress

  return {
    id: 'address',
    name: 'Address',
    status: hasAddress ? 'good' : 'critical',
    statusMessage: hasAddress ? 'Address set' : 'No address',
    explainer: hasAddress
      ? 'Address enables local search visibility and open now results'
      : "Google can't properly locate your business without an address",
    ctaLabel: hasAddress ? '' : 'Add address',
    // ctaAction: hasAddress ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: true,
  }
}

const scoreWebsite = (data: BusinessProfileData): ScoredItem => {
  const hasWebsite = data.hasWebsite

  return {
    id: 'website',
    name: 'Website',
    status: hasWebsite ? 'good' : 'critical',
    statusMessage: hasWebsite ? 'Website set' : 'Not set',
    explainer: hasWebsite
      ? 'Website link allows clients to access your site and improves trust signals'
      : "Without a website link clients can't reach you online",
    ctaLabel: hasWebsite ? '' : 'Add website',
    // ctaAction: hasWebsite ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: true,
  }
}

const scoreVerification = (data: BusinessProfileData): ScoredItem => {
  const isVerified = data.isVerified

  return {
    id: 'verification',
    name: 'Verification',
    status: isVerified ? 'good' : 'critical',
    statusMessage: isVerified ? 'Verification complete' : 'Incomplete',
    explainer: isVerified
      ? 'Verified listings have higher visibility and increased client trust'
      : 'Unverified listings are not as visible on google searches',
    ctaLabel: isVerified ? '' : 'Check on verification',
    // ctaAction: isVerified ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: true,
  }
}

const scoreOpeningHours = (data: BusinessProfileData): ScoredItem => {
  const hasHours = data.hasOpeningHours

  return {
    id: 'openingHours',
    name: 'Opening Hours',
    status: hasHours ? 'good' : 'critical',
    statusMessage: hasHours ? 'Hours configured' : 'Not set',
    explainer: hasHours
      ? "Clients can easily see when you're available, reducing enquiry friction"
      : 'You won\'t appear in "open now" searches—a major discovery channel',
    ctaLabel: hasHours ? '' : 'Add opening hours',
    // ctaAction: hasHours ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: true,
  }
}

const scoreServices = (data: BusinessProfileData): ScoredItem => {
  const count = data.servicesCount

  let status: MetricStatus
  if (count === 0) status = 'critical'
  else if (count <= 5) status = 'needs_attention'
  else if (count <= 15) status = 'good'
  else status = 'great'

  return {
    id: 'services',
    name: 'Services',
    status,
    statusMessage: count === 1 ? '1 service' : ['$0 services', count],
    explainer: 'Adding services increases your profile visibility for different search terms, aim for 15 or more relevant service tags',
    ctaLabel: status === 'critical' || status === 'needs_attention' ? 'Add services' : '',
    // ctaAction: status === 'critical' || status === 'needs_attention' ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: true,
  }
}

const scorePrimaryCategory = (data: BusinessProfileData): ScoredItem => {
  const hasCategory = data.hasPrimaryCategory

  return {
    id: 'primaryCategory',
    name: 'Primary Category',
    status: hasCategory ? 'good' : 'critical',
    statusMessage: hasCategory ? 'Primary Category set' : 'Not set',
    explainer: hasCategory
      ? 'Primary category helps google match your business to relevant searches'
      : 'Without a primary category it is difficult for google to recognise what your business does',
    ctaLabel: hasCategory ? '' : 'Add primary category',
    // ctaAction: hasCategory ? () => {} : undefined,
    statusComponent: 'statusIcon',
    minimizeComplete: true,
  }
}

const scoreReviewsCategory = (data: BusinessProfileData): ScoredCategory => {
  const items = [
    scoreReviewsWithText(data),
    scoreReviewConsistency(data),
    scoreAverageRating(data),
    scoreReviewResponseRate(data),
  ]

  return {
    id: 'reviews',
    name: 'Reviews',
    progress: calculateProgress(items),
    issueCount: countIssues(items),
    items,
  }
}

const scoreContentCategory = (data: BusinessProfileData): ScoredCategory => {
  const items = [
    scorePhotoCount(data),
    scoreClientPhotoCount(data),
    scorePhotoRecency(data),
    scorePostCount(data),
    scorePostRecency(data),
  ]

  return {
    id: 'content',
    name: 'Content & Engagement',
    progress: calculateProgress(items),
    issueCount: countIssues(items),
    items,
  }
}

const scoreCompletenessCategory = (data: BusinessProfileData): ScoredCategory => {
  const items = [
    scoreDescription(data),
    scoreAddress(data),
    scoreWebsite(data),
    scoreVerification(data),
    scoreOpeningHours(data),
    scoreServices(data),
    scorePrimaryCategory(data),
  ]

  return {
    id: 'completeness',
    name: 'Profile Completeness',
    progress: calculateProgress(items),
    issueCount: countIssues(items),
    items,
  }
}

export const transformBusinessProfileData = (data: BusinessProfileData): ScoredCategory[] => {
  return [
    scoreReviewsCategory(data),
    scoreContentCategory(data),
    scoreCompletenessCategory(data),
  ]
}

export const getStatusColor = (status: MetricStatus): 'error' | 'warning' | 'success' | 'primary' => {
  switch (status) {
    case 'critical':
      return 'error'
    case 'needs_attention':
      return 'warning'
    case 'good':
      return 'success'
    case 'great':
      return 'success'
  }
}

export const getProgressColor = (progress: number): 'error' | 'warning' | 'success' | 'primary' => {
  if (progress === 100) return 'success'
  if (progress >= 80) return 'success'
  if (progress >= 50) return 'warning'
  return 'error'
}

export const getProgressLabel = (progress: number): string => {
  if (progress === 100) return 'Outstanding'
  if (progress >= 80) return 'Good'
  if (progress >= 50) return 'Needs work'
  return 'Critical issues'
}
